import React, { useRef } from 'react'
import { Formik } from 'formik'
import axios from 'axios'
import { Form as RBForm } from 'react-bootstrap'
import classNames from 'classnames'
import { useParams } from 'react-router-dom'
import {
  useResolutionService,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import Imgix from 'react-imgix'
import API from '../../utils/API'
import ExperienceInputField from '../form/ExperienceInputField'
import ExperienceCKEditor from './ExperienceCKEditor'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const JourneyEditor = ({
  journey,
  journeyIndex,
  onSave,
  onCancel,
  isSaving = false,
  onUploadingChange,
}) => {
  const { isMDScreen } = useResolutionService()
  const imageRef = useRef()
  const { questId } = useParams()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const inputResetRef = useRef(null)
  const [showImageCropModal, setShowImageCropModal] = React.useState(false)
  const [selectedImageFile, setSelectedImageFile] = React.useState(null)
  const [isDraggingImage, setIsDraggingImage] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] =
    React.useState(false)
  const pendingRemoveRef = useRef(null)

  const setUploading = (value) => {
    setIsUploading(value)
    if (onUploadingChange) onUploadingChange(value)
  }

  const handlePosterClick = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const uploadQuestImage = async (imageFile) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()

      const { data } = await API.AdminAPI.quests.getJourneySignedURL(
        questId,
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })
        return data.fileName
      } else {
        throw new Error('Image upload failed')
      }
    } catch (e) {
      throw e
    }
  }

  const initialValues = {
    title: journey?.title || '',
    description: journey?.description || '',
    body: journey?.body || '',
    imageFileName: journey?.imageFileName || '',
    isUploading: false,
  }

  const handleFormSubmit = async (values) => {
    try {
      if (onSave) {
        onSave({
          ...values,
          sequenceNumber: journeyIndex + 1,
        })
      }
    } catch (e) {
      Error.showError(e)
    }
  }

  const handleRemoveImage = async (setFieldValue) => {
    try {
      setFieldValue('imageFileName', '')
    } catch (e) {
      Notification.showNotification('Failed to remove image', 'danger')
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}>
      {(formik) => {
        const { handleSubmit, setFieldValue, values } = formik
        const hasImage = !!values.imageFileName
        const busy = values?.isUploading || isUploading

        return (
          <form
            id='journey-editor-form'
            onSubmit={handleSubmit}
            aria-busy={busy || undefined}>
            <div className='experience-journey-editor'>
              <div>
                <ExperienceInputField
                  name='title'
                  label='Section title'
                  placeholder='Add section title'
                  capitalizeFirstLetter={true}
                  showErrorMessage={false}
                  displayFlex={false}
                  disabled={busy}
                />
                <div className='mt-12px'>
                  <ExperienceInputField
                    name='description'
                    label='Title'
                    capitalizeFirstLetter={true}
                    placeholder='Add title'
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                </div>
                <div className='mt-12px'>
                  <label className='experience-f-label'>Body</label>
                  <ExperienceCKEditor
                    name='body'
                    data={values.body || ''}
                    disabled={busy}
                    placeholder='Add the phase description'
                    onReady={(editor) => {
                      if (editor) {
                        editor.editing.view.change((writer) => {
                          writer.setStyle(
                            'height',
                            '250px',
                            editor.editing.view.document.getRoot(),
                          )
                        })
                      }
                    }}
                    onChange={(event, editor) => {
                      if (busy) {
                        return
                      }
                      const data = editor.getData()
                      setFieldValue('body', data)
                    }}
                    onBlur={(event, editor) => {
                      const data = editor.getData()
                      setFieldValue('body', data)
                    }}
                  />
                </div>
              </div>

              <div className='experience-f'>
                <label className='experience-f-label'>Journey image</label>
                <div className='experience-img-slot' data-ratio='2:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasImage && !busy,
                      'is-dragging': isDraggingImage,
                    })}
                    style={{
                      minHeight: isMDScreen ? '200px' : '150px',
                    }}
                    onClick={() => {
                      if (!busy) handlePosterClick()
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!busy) handlePosterClick()
                      }
                    }}>
                    {busy ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        Uploading…
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasImage
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}

                    {hasImage ? (
                      values.imageFileName.startsWith('data:') ? (
                        <img src={values.imageFileName} alt='journey' />
                      ) : (
                        <Imgix
                          src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${values.imageFileName}`}
                          imgixParams={{
                            fit: 'crop',
                            crop: 'center',
                            ar: isMDScreen ? '2:1' : '4:2',
                          }}
                          width={'100%'}
                          height={'100%'}
                          alt='journey'
                          className={classNames('lazyload', {
                            itenaryImage: busy,
                          })}
                          style={{
                            objectFit: 'cover',
                            aspectRatio: isMDScreen ? '2 / 1' : '4 / 2',
                          }}
                        />
                      )
                    ) : !busy ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 2:1</small>
                      </>
                    ) : null}

                    {hasImage && !busy ? (
                      <button
                        type='button'
                        className='experience-img-remove'
                        aria-label='Remove image'
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          pendingRemoveRef.current = () =>
                            handleRemoveImage(setFieldValue)
                          setShowRemoveImageConfirm(true)
                        }}>
                        ×
                      </button>
                    ) : null}

                    <RBForm.Control
                      type='file'
                      size='lg'
                      placeholder='Select File'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={async (e) => {
                        const target = e.target
                        const files = e.target.files
                        if (files && files?.length > 0) {
                          openCropModal(files[0], () => {
                            if (target) target.value = ''
                          })
                        }
                      }}
                      style={{ display: 'none' }}
                      name='imageFileName'
                      sm='10'
                      ref={imageRef}
                    />
                  </div>
                </div>
              </div>
            </div>

            <ImageCropModal
              show={showImageCropModal}
              onHide={handleCropModalClose}
              file={selectedImageFile}
              onConfirm={async (croppedImageFile) => {
                if (!croppedImageFile) return
                setShowImageCropModal(false)
                setFieldValue('isUploading', true)
                setUploading(true)
                const reader = new FileReader()
                reader.onloadend = async () => {
                  setFieldValue('imageFileName', reader.result)
                  try {
                    const fileName = await uploadQuestImage(croppedImageFile)
                    setFieldValue('imageFileName', fileName)
                    setFieldValue('isUploading', false)
                    setUploading(false)
                  } catch (e) {
                    setFieldValue('isUploading', false)
                    setUploading(false)
                    Notification.showNotification(
                      'Image upload failed',
                      'danger',
                    )
                  } finally {
                    setSelectedImageFile(null)
                    setIsDraggingImage(false)
                    resetImageInput()
                  }
                }
                reader.readAsDataURL(croppedImageFile)
              }}
              isUploading={values?.isUploading}
              aspect={2 / 1}
              ratioLabel='2:1'
              title='Crop Image'
            />

            <div className='experience-form-actions'>
              <ExperienceButton
                type='submit'
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={isSaving || busy}>
                Save section
              </ExperienceButton>
              <ExperienceButton
                type='button'
                variant='outline'
                size='sm'
                onClick={onCancel}
                disabled={isSaving || busy}>
                Cancel
              </ExperienceButton>
            </div>
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => {
                setShowRemoveImageConfirm(false)
                pendingRemoveRef.current = null
              }}
              onConfirm={() => {
                const run = pendingRemoveRef.current
                pendingRemoveRef.current = null
                setShowRemoveImageConfirm(false)
                if (run) run()
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </form>
        )
      }}
    </Formik>
  )
}

export default JourneyEditor
